/* eslint-disable jsx-a11y/no-noninteractive-tabindex -- The table region is focusable for horizontal keyboard scrolling. */
import { useEffect, useRef, useState } from "react";
import { LuPlus, LuImage } from "react-icons/lu";
import { getProduct, updateVariant } from "../../../../../api/products";
import { OptionsHandler } from "./options/optionsHandler";
const label = (variant) =>
  (variant.options || []).map((o) => o.name).join(" / ") || "Variante";
export const ReferenceVariants = ({
  data,
  registerSave,
  onDirty,
  disabled,
}) => {
  const [rows, setRows] = useState(data?.variants || []),
    [options, setOptions] = useState(data?.options || []),
    [selected, setSelected] = useState([]),
    [amount, setAmount] = useState(""),
    [open, setOpen] = useState(false),
    [groupId, setGroupId] = useState(null);
  const baseline = useRef(JSON.stringify(data?.variants || []));
  const [refreshError, setRefreshError] = useState("");
  const change = (id, patch) => {
    setRows((list) =>
      list.map((row) => (row.variantId === id ? { ...row, ...patch } : row)),
    );
    onDirty();
  };
  useEffect(
    () =>
      registerSave("variants", async () => {
        if (JSON.stringify(rows) === baseline.current) return;
        const before = JSON.parse(baseline.current);
        for (const row of rows) {
          const original = before.find((v) => v.variantId === row.variantId);
          if (
            original?.price === row.price &&
            original?.isActive === row.isActive &&
            !row.file
          )
            continue;
          const price = Number(String(row.price).replace(",", "."));
          if (!Number.isFinite(price) || price < 0)
            throw Error(
              "Revisa el suplemento de las variantes: debe ser un número positivo o cero.",
            );
          const response = await updateVariant(row.variantId, {
            price,
            isActive: row.isActive,
            files: row.file ? [row.file] : [],
          });
          if (response?.error !== false)
            throw Error(
              "No se pudieron guardar todas las variantes. Tus cambios se conservan; vuelve a guardar.",
            );
        }
        baseline.current = JSON.stringify(rows);
      }),
    [rows, registerSave],
  );
  const batch = (kind) => {
    const value = Number(amount.replace(",", "."));
    if (
      !selected.length ||
      !Number.isFinite(value) ||
      value < 0 ||
      amount === ""
    )
      return;
    setRows((list) =>
      list.map((row) =>
        selected.includes(row.variantId)
          ? {
              ...row,
              price: kind === "sum" ? Number(row.price || 0) + value : value,
            }
          : row,
      ),
    );
    onDirty();
  };
  const closeOptions = async () => {
    setOpen(false);
    try {
      const result = await getProduct(data.productId);
      if (result?.error !== false) throw Error();
      setRows(result.data.variants || []);
      setOptions(result.data.options || []);
      setSelected([]);
      baseline.current = JSON.stringify(result.data.variants || []);
      setRefreshError("");
    } catch {
      setRefreshError(
        "No se pudieron actualizar las variantes. Vuelve a abrir la configuración de opciones.",
      );
    }
  };
  const optionGroups = Object.values(
    options.reduce((groups, option) => {
      const id = option.optionGroupId;
      groups[id] ??= {
        id,
        name: option.optionGroup?.name || "Opción",
        values: [],
      };
      groups[id].values.push(option);
      return groups;
    }, {}),
  );
  const hasDraft = JSON.stringify(rows) !== baseline.current;
  const configure = (id = null) => {
    setGroupId(id);
    setOpen(true);
  };
  const matchesValue = (row, option) =>
    !!option.optionId &&
    (row.options || []).some((value) => value.optionId === option.optionId);
  const setValuePrice = (option, price) => {
    setRows((list) =>
      list.map((row) => (matchesValue(row, option) ? { ...row, price } : row)),
    );
    onDirty();
  };
  return (
    <>
      <section className="productFormCard referenceVariants referenceOptionsCard">
        <header>
          <h2>Opciones</h2>
          <p>
            Diferentes versiones del producto. De ellas salen las variantes.
          </p>
        </header>
        <div className="productCardFields">
          {!optionGroups.length && (
            <p className="referenceEmpty">Este producto no tiene opciones.</p>
          )}
          <div className="referenceOptionSummary">
            {optionGroups.map((group) => (
              <section key={group.id}>
                <span className="referenceOptionLabel">
                  Nombre de la opción
                </span>
                <button
                  type="button"
                  className="referenceOptionName"
                  aria-label={`Configurar opción ${group.name}`}
                  disabled={disabled || hasDraft}
                  onClick={() => configure(group.id)}
                >
                  {group.name}
                </button>
                <span className="referenceOptionLabel">Valores</span>
                <div>
                  {group.values.map((value, index) => (
                    <span
                      key={value.optionId || index}
                      className="referenceChip"
                    >
                      {value.name}
                    </span>
                  ))}
                  <button
                    type="button"
                    className="referenceAddValue"
                    disabled={disabled || hasDraft}
                    onClick={() => configure(group.id)}
                  >
                    <LuPlus /> Añadir valor
                  </button>
                </div>
              </section>
            ))}
          </div>
          <button
            type="button"
            className="referenceOutline"
            disabled={disabled || JSON.stringify(rows) !== baseline.current}
            onClick={() => configure()}
          >
            <LuPlus />
            Añadir opción
          </button>
          {optionGroups.length > 0 && (
            <p className="referenceHelp">
              Pulsa el nombre para editar o quitar opciones en el configurador
              compartido.
            </p>
          )}
          {hasDraft && (
            <p className="referenceHelp">
              Guarda los suplementos antes de modificar las opciones.
            </p>
          )}
          {refreshError && <p role="alert">{refreshError}</p>}
        </div>
      </section>
      {(rows.length > 0 || optionGroups.length > 0) && (
        <section className="productFormCard referenceVariants referenceGeneratedVariants">
          <header>
            <h2>Variantes</h2>
            <p>
              Se generan a partir de las opciones. El suplemento sin IVA se suma
              al precio base.
            </p>
          </header>
          <div className="productCardFields">
            {!rows.length ? (
              <p className="referenceEmpty">
                No hay variantes generadas para estas opciones.
              </p>
            ) : (
              <>
                {optionGroups.map((group) => (
                  <div className="referenceValuePrices" key={group.id}>
                    <div className="referenceValuePricesHeading">
                      <strong>
                        Suplemento
                        {optionGroups.length > 1 ? ` · ${group.name}` : ""}
                      </strong>
                      <span>Aplicado a todas las variantes del valor</span>
                    </div>
                    <div className="referenceValuePricesGrid">
                      {group.values.map((option, index) => {
                        const variants = rows.filter((row) =>
                          matchesValue(row, option),
                        );
                        const same =
                          variants.length > 0 &&
                          variants.every(
                            (row) =>
                              String(row.price ?? 0) ===
                              String(variants[0].price ?? 0),
                          );
                        return (
                          <label key={option.optionId || index}>
                            <span>
                              <strong>{option.name}</strong>
                              <small>
                                {variants.length}{" "}
                                {variants.length === 1
                                  ? "variante"
                                  : "variantes"}{" "}
                                ·{" "}
                                {same
                                  ? `${variants[0].price ?? 0} €`
                                  : "Suplementos diferentes"}
                              </small>
                            </span>
                            <span className="referenceMoneyInput">
                              <input
                                aria-label={`Suplemento de todas las variantes con ${group.name}: ${option.name}`}
                                inputMode="decimal"
                                disabled={disabled || !variants.length}
                                value={same ? (variants[0].price ?? 0) : ""}
                                placeholder={variants.length ? "Varios" : "—"}
                                onChange={(event) =>
                                  setValuePrice(option, event.target.value)
                                }
                              />
                              <span>€</span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
                <div className="referenceVariantCollection">
                  <div className="referenceVariantTools">
                    <h3>Variantes</h3>
                    <span>
                      {selected.length
                        ? `${selected.length} seleccionadas`
                        : "Ninguna variante seleccionada"}
                    </span>
                    <button
                      type="button"
                      disabled={disabled || !selected.length}
                      onClick={() => {
                        setSelected([]);
                      }}
                    >
                      Quitar selección
                    </button>
                    {[true, false].map((active) => (
                      <button
                        key={String(active)}
                        type="button"
                        disabled={disabled || !selected.length}
                        onClick={() => {
                          setRows((list) =>
                            list.map((row) =>
                              selected.includes(row.variantId)
                                ? { ...row, isActive: active }
                                : row,
                            ),
                          );
                          onDirty();
                        }}
                      >
                        {active ? "Activar" : "Desactivar"}
                      </button>
                    ))}
                    <input
                      aria-label="Importe para las variantes seleccionadas"
                      inputMode="decimal"
                      placeholder="0,00"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                    />
                    <span>€</span>
                    <button
                      type="button"
                      disabled={disabled || !selected.length || amount === ""}
                      onClick={() => batch("set")}
                    >
                      Fijar
                    </button>
                    <button
                      type="button"
                      disabled={disabled || !selected.length || amount === ""}
                      onClick={() => batch("sum")}
                    >
                      Sumar
                    </button>
                  </div>
                  <div
                    className="referenceVariantTable"
                    role="region"
                    aria-label="Tabla de variantes"
                    tabIndex={0}
                  >
                    <table>
                      <thead>
                        <tr>
                          <th>
                            <input
                              type="checkbox"
                              aria-label="Seleccionar todas las variantes"
                              disabled={disabled}
                              checked={
                                rows.length > 0 &&
                                selected.length === rows.length
                              }
                              onChange={(e) =>
                                setSelected(
                                  e.target.checked
                                    ? rows.map((v) => v.variantId)
                                    : [],
                                )
                              }
                            />
                          </th>
                          <th>Variante</th>
                          <th>Imagen</th>
                          <th>Suplemento</th>
                          <th>Activa</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row) => (
                          <tr key={row.variantId}>
                            <td>
                              <input
                                type="checkbox"
                                aria-label={`Seleccionar ${label(row)}`}
                                disabled={disabled}
                                checked={selected.includes(row.variantId)}
                                onChange={() =>
                                  setSelected((ids) =>
                                    ids.includes(row.variantId)
                                      ? ids.filter((id) => id !== row.variantId)
                                      : [...ids, row.variantId],
                                  )
                                }
                              />
                            </td>
                            <th scope="row">{label(row)}</th>
                            <td>
                              <label className="referenceVariantImage">
                                {row.preview || row.images?.[0] ? (
                                  <img
                                    src={row.preview || row.images[0]}
                                    alt={label(row)}
                                  />
                                ) : (
                                  <>
                                    <LuImage />
                                    Añadir imagen
                                  </>
                                )}
                                <input
                                  type="file"
                                  aria-label={`Imagen de ${label(row)}`}
                                  accept="image/png,image/jpeg,image/webp"
                                  disabled={disabled}
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;
                                    const reader = new FileReader();
                                    reader.onload = () =>
                                      change(row.variantId, {
                                        file,
                                        preview: reader.result,
                                      });
                                    reader.readAsDataURL(file);
                                  }}
                                />
                              </label>
                            </td>
                            <td>
                              <input
                                aria-label={`Suplemento de ${label(row)}`}
                                inputMode="decimal"
                                disabled={disabled}
                                value={row.price ?? 0}
                                onChange={(e) =>
                                  change(row.variantId, {
                                    price: e.target.value,
                                  })
                                }
                              />
                              <span> €</span>
                            </td>
                            <td>
                              <label className="referenceVariantSwitch">
                                <input
                                  type="checkbox"
                                  role="switch"
                                  aria-label={`Activar ${label(row)}`}
                                  disabled={disabled}
                                  checked={!!row.isActive}
                                  onChange={(e) =>
                                    change(row.variantId, {
                                      isActive: e.target.checked,
                                    })
                                  }
                                />
                                <span>
                                  {row.isActive ? "Activa" : "Inactiva"}
                                </span>
                              </label>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <p className="referenceHelp">
                  Los cambios de suplemento se aplican al producto al guardar la
                  ficha.
                </p>
              </>
            )}
          </div>
        </section>
      )}
      {open && (
        <OptionsHandler
          isOpen
          initialGroupId={groupId}
          togglePopup={closeOptions}
        />
      )}
    </>
  );
};
